/**
 * 🚚 Переезд бота с другой франшизы.
 *
 * Партнёр приносит своего бота (тот же @username, новым токеном), базу клиентов
 * выгрузкой и, если захочет, действующие подписки. Переезжать есть смысл только с
 * клиентами, поэтому экран сначала спрашивает, есть ли бот с клиентами на другой
 * платформе, а первым шагом идёт база: без неё переезда нет (бэк отвечает
 * `rows_required`). Дальше бот, подписки (решает платформа, потом партнёр видит сумму
 * и платит) и сам перенос. Раскрыт только текущий шаг, пройденные свёрнуты.
 *
 * Без `?id` экран задаёт вопрос, `?begin=true` открывает загрузку базы (черновик
 * заводится при выборе файла), с `?id` ведёт один переезд. Ссылка с `?id` приходит и
 * в сообщении франш-бота («🚚 Открыть переезд»).
 *
 * Про поставщика партнёр не узнаёт ничего: ни цены за подписку, ни дней, ни того,
 * как делится сумма. Только итог «перенос N подписок со скидкой обойдётся в X ₽».
 *
 * Копия на сайте: partner-site/src/routes/vpn/app/bot-import.tsx.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileUp,
  Loader2,
  Megaphone,
  PauseCircle,
  Tags,
  Truck,
  X,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  api,
  type BotImport,
  type BotImportMapping,
  type BotImportRole,
  type BotImportRowsPreview,
  type BotImportStatus,
} from '@/lib/api';
import { InvoicePay } from '@/components/InvoicePay';
import { Skeleton, SkeletonCard, SkeletonLine } from '@/components/ui/skeleton';
import { getWebApp, haptic } from '@/lib/telegram';
import { cn } from '@/lib/utils';

type Search = { id?: string; begin?: true };

export const Route = createFileRoute('/bot-import')({
  // Без объявления роутер выбросил бы `id` на своём переходе, и ссылка из сообщения
  // франш-бота открывала бы начало переезда вместо самого переезда.
  validateSearch: (s: Record<string, unknown>): Search => {
    if (typeof s.id === 'string' && s.id) return { id: s.id };
    return s.begin === true || s.begin === 'true' ? { begin: true } : {};
  },
  component: BotImportPage,
});

/** Переезд ещё идёт: бота нельзя передать или удалить, второй переезд не открыть. */
const OPEN_STATUSES: ReadonlySet<BotImportStatus> = new Set([
  'draft',
  'awaiting_decision',
  'awaiting_payment',
  'running',
  'paused',
]);

/** Ход за платформой: решение по подпискам, приход оплаты счётом, перенос воркером.
 *  В остальных статусах ждём действий партнёра, и дёргать бэк незачем. */
const POLL_STATUSES: ReadonlySet<BotImportStatus> = new Set([
  'awaiting_decision',
  'awaiting_payment',
  'running',
  'paused',
]);
const POLL_MS = 5_000;

/** Базу можно заменить, пока за перенос не заплатили и ни одна подписка не выдана. */
const UPLOAD_STATUSES: ReadonlySet<BotImportStatus> = new Set([
  'draft',
  'awaiting_decision',
  'awaiting_payment',
]);

const STATUS_LABEL: Record<BotImportStatus, string> = {
  draft: 'Настройка',
  awaiting_decision: 'Ждёт нашего решения',
  awaiting_payment: 'Ждёт оплаты',
  running: 'Переносим',
  paused: 'На паузе',
  done: 'Завершён',
  cancelled: 'Отменён',
};

/** Порядок шагов: база первой, это и есть причина переезжать. */
const STEPS = [
  { key: 'base', title: 'База клиентов', short: 'База' },
  { key: 'bot', title: 'Бот', short: 'Бот' },
  { key: 'subs', title: 'Подписки', short: 'Подписки' },
  { key: 'transfer', title: 'Перенос', short: 'Перенос' },
] as const;
type StepKey = (typeof STEPS)[number]['key'];

const ROLES: { role: BotImportRole; label: string }[] = [
  { role: 'tg_id', label: 'Telegram ID' },
  { role: 'username', label: 'Ник' },
  { role: 'end_date', label: 'Подписка до' },
  { role: 'days_left', label: 'Осталось дней' },
  { role: 'devices', label: 'Устройств' },
  // Тариф важнее колонки устройств: в выгрузках она часто считает подключённые
  // устройства, и «Про» с одним телефоном переехал бы «Стандартом».
  { role: 'plan', label: 'Тариф' },
  { role: 'balance', label: 'Баланс' },
];

/** Тот же формат, что проверяет онбординг: цифры, двоеточие, длинный хвост. */
const TOKEN_RE = /^\d{6,12}:[A-Za-z0-9_-]{30,}$/;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
/** Расширения и MIME вместе: на части Android выбор по одним расширениям не даёт
 *  выбрать CSV вовсе, потому что системе неизвестен его тип. */
const FILE_ACCEPT = [
  '.csv',
  '.xlsx',
  '.xls',
  '.json',
  '.txt',
  'text/csv',
  'text/plain',
  'application/json',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
].join(',');

const FILE_TOO_BIG = 'Файл больше 10 МБ. Уберите из выгрузки лишние колонки или сохраните её в CSV';

/**
 * Ошибки бэка переезда приходят кодом с английским текстом для логов, поэтому
 * человеку отвечаем по коду. Русский текст бэка (разбор файла, занятый ник)
 * показываем как есть.
 */
// `already_open` и `state` сюда не входят: у них несколько причин, и бэк отвечает
// точным русским текстом («После оплаты базу уже не заменить…»), его и показываем.
const ERROR_TEXT: Record<string, string> = {
  'app.bots.invalid_token': 'Токен не подошёл: проверьте, что скопировали его целиком',
  'app.bots.already_registered':
    'Этот бот уже подключён к платформе. Если он ваш, выберите его в списке уже подключённых',
  'app.bots.limit_reached':
    'Лимит ботов в кабинете исчерпан. Удалите бота, которым не пользуетесь, или попросите поддержку увеличить лимит',
  'app.bot_import.bot_unsuitable':
    'Этого бота нельзя подключить к переезду. Выберите другого или подключите бота новым токеном',
  'app.bot_import.nothing_to_transfer': 'В базе нет действующих подписок, переносить нечего',
  'app.bot_import.rows_required': 'Сначала загрузите базу клиентов',
  'app.bot_import.insufficient_balance': 'На балансе не хватает: оплатите перенос счётом',
  'app.bot_import.not_found': 'Переезд не найден',
};

const CYRILLIC = /[А-Яа-яЁё]/;

function errCode(e: unknown): string | undefined {
  return (e as { body?: { error?: { code?: string } } } | null)?.body?.error?.code;
}

function errorText(e: unknown, fallback: string): string {
  const code = errCode(e);
  if (code && ERROR_TEXT[code]) return ERROR_TEXT[code];
  // Лимит тела на прокси отвечает 413 без нашего JSON.
  if ((e as { status?: number } | null)?.status === 413) return FILE_TOO_BIG;
  const msg = e instanceof Error ? e.message : '';
  return msg && CYRILLIC.test(msg) ? msg : fallback;
}

/** Переезд после любого действия: кладём ответ в кэш экрана и освежаем список. */
function setImport(qc: QueryClient, imp: BotImport): void {
  qc.setQueryData(['bot-import', imp.id], imp);
  void qc.invalidateQueries({ queryKey: ['bot-imports'] });
}

/** Ответ «уже другой шаг» значит, что экран отстал: перечитываем переезд. */
function refreshOnState(qc: QueryClient, e: unknown, id: string): void {
  if (errCode(e) === 'app.bot_import.state') {
    void qc.invalidateQueries({ queryKey: ['bot-import', id] });
  }
}

/** Русское склонение: 1 подписка, 2 подписки, 5 подписок, 21 подписка. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** Сумма в валюте показа из минорных единиц: 456000 → «4 560 ₽». */
function fmtMoney(displayMinor: number, currency: string): string {
  const sym = currency === 'RUB' ? '₽' : currency;
  return `${Math.round(displayMinor / 100).toLocaleString('ru-RU')} ${sym}`;
}

function fmtBytes(n: number): string {
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} КБ`;
  return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

function canUploadRows(imp: BotImport): boolean {
  if (imp.paid_display_minor != null || imp.subs_done > 0) return false;
  // Закрытый переезд без подписок базу тоже принимает: выгрузка могла найтись уже
  // после того, как бот переехал.
  return UPLOAD_STATUSES.has(imp.status) || (imp.status === 'done' && imp.payer === 'none');
}

/** Шаг пройден: база загружена (или старый переезд закрылся без неё), бот подключён,
 *  вопрос с подписками закрыт, перенос закончен. */
function stepDone(imp: BotImport, key: StepKey): boolean {
  if (key === 'base') return imp.rows > 0 || imp.wants_transfer === false;
  if (key === 'bot') return imp.bot_id !== null;
  if (key === 'subs') return subsSettled(imp);
  return imp.status === 'done';
}

/** Текущий шаг: первый непройденный по порядку. У старых переездов, где бота
 *  подключали первым, а базу не грузили, это тоже база. */
function currentStep(imp: BotImport): StepKey {
  return STEPS.find((s) => !stepDone(imp, s.key))?.key ?? 'transfer';
}

/** Вопрос с подписками закрыт: решено, кто платит, и платный перенос оплачен. */
function subsSettled(imp: BotImport): boolean {
  // Перенос уже идёт или закончен: вопрос закрыт, даже если сумму оплаты бэк не прислал.
  if (imp.status === 'running' || imp.status === 'paused' || imp.status === 'done') return imp.payer !== null;
  return imp.payer !== null && (imp.payer !== 'paid' || imp.paid_display_minor != null);
}

// ---------------------------------------------------------------- экран

function BotImportPage() {
  const { id } = Route.useSearch();
  const navigate = useNavigate();
  const { data: partner } = useQuery({ queryKey: ['partner'], queryFn: () => api.me() });

  const { begin } = Route.useSearch();

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    tg.MainButton.hide();
    // С открытого переезда и с загрузки базы назад к вопросу, с вопроса в «Боты».
    const onBack = () => {
      if (id || begin) void navigate({ to: '/bot-import', search: {} });
      else void navigate({ to: '/bots' });
    };
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate, id, begin]);

  // Делегату бэк на всё ответит 403: переезд это деньги и вся база клиентов.
  if (partner?.isDelegate) return <OwnerOnly />;
  if (id) return <ImportScreen key={id} id={id} />;
  return begin ? <BeginScreen /> : <StartScreen />;
}

function PageTitle({ children }: { children?: ReactNode }) {
  return (
    <div className="px-1 pt-2">
      <h2 className="font-display text-[22px] font-medium">Переезд бота</h2>
      {children}
    </div>
  );
}

function OwnerOnly() {
  return (
    <div className="p-4 space-y-4 pb-32">
      <PageTitle />
      <div className="surface-card rounded-[22px] py-10 px-6 text-center">
        <div className="w-[52px] h-[52px] rounded-[16px] bg-white/[0.05] flex items-center justify-center mx-auto mb-3.5 text-dim">
          <Truck className="w-[26px] h-[26px]" strokeWidth={1.6} />
        </div>
        <div className="font-bold text-[15px] text-muted-foreground">Переезд доступен владельцу кабинета</div>
        <div className="text-[12.5px] text-dim mt-1.5 leading-snug">
          В нём оплата переноса подписок и вся база клиентов. Попросите владельца начать переезд.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- начало переезда

function StartScreen() {
  const navigate = useNavigate();
  const listQ = useQuery({
    queryKey: ['bot-imports'],
    queryFn: () => api.botImports(),
    staleTime: 0,
  });
  const imports = listQ.data ?? [];
  const open = imports.filter((i) => OPEN_STATUSES.has(i.status));
  const past = imports.filter((i) => !OPEN_STATUSES.has(i.status));

  return (
    <div className="p-4 space-y-5 pb-32">
      <PageTitle>
        <p className="mt-1 text-[13px] text-dim leading-snug">
          Для тех, у кого уже есть VPN-бот с клиентами на другой платформе.
        </p>
      </PageTitle>

      {open.length > 0 && <ImportList title="Незавершённые переезды" items={open} />}

      <section className="surface-card rounded-[20px] p-4 space-y-4">
        <h3 className="font-display text-[17px] font-medium leading-snug">
          У вас уже есть VPN-бот с клиентами на другой платформе?
        </h3>
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => {
              haptic('light');
              void navigate({ to: '/bot-import', search: { begin: true } });
            }}
            className="btn-glass-red w-full h-12 rounded-[13px] text-[15px] font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          >
            Да, переезжаю
          </button>
          <button
            type="button"
            onClick={() => {
              haptic('light');
              void navigate({ to: '/onboarding/bot' });
            }}
            className="glass glass-flat w-full h-12 rounded-[13px] text-[15px] font-semibold active:scale-[0.98] transition-transform"
          >
            Нет, начинаю с нуля
          </button>
        </div>
        <p className="text-[12px] text-dim leading-snug">
          С нуля переезжать неоткуда: создайте бота, это пара минут.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="px-1 text-[12.5px] text-dim">Что понадобится</h3>
        <div className="surface-card rounded-[20px] p-4">
          <ol className="space-y-3">
            <ChecklistItem n={1}>
              Выгрузка клиентов из старой панели: CSV, Excel, JSON или TXT. Хватит колонки с
              Telegram ID, даты окончания подписок и тариф по желанию.
            </ChecklistItem>
            <ChecklistItem n={2}>
              Доступ к боту в @BotFather: выпустите новый токен, и старая платформа потеряет
              бота.
            </ChecklistItem>
          </ol>
          <p className="mt-3 text-[12px] text-dim leading-snug">
            Клиенты останутся в том же боте. Тем, у кого есть действующая подписка, бот
            пришлёт новую ссылку.
          </p>
        </div>
      </section>

      {past.length > 0 && <ImportList title="Прошлые переезды" items={past} />}
    </div>
  );
}

/**
 * Шаг 1 до появления переезда: откуда переезжаете и база. Черновик заводится на выборе
 * файла (разбору нужен адрес переезда), а пустой брошенный черновик, если такой уже
 * есть, берётся снова: иначе каждый заход сюда занимал бы место под потолком из трёх.
 */
function BeginScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [source, setSource] = useState('');
  const idRef = useRef<string | null>(null);
  const creating = useRef<Promise<string> | null>(null);
  // Название уходит в черновик при его создании: после этого поле только для чтения,
  // иначе правка молча не дошла бы.
  const [created, setCreated] = useState(false);

  const ensureId = (): Promise<string> => {
    if (idRef.current) return Promise.resolve(idRef.current);
    creating.current ??= (async () => {
      // Список берём свежим: по кэшу можно было не заметить пустой черновик и завести
      // второй, или взять черновик, куда базу уже загрузили с другого устройства.
      const fresh = await qc.fetchQuery({
        queryKey: ['bot-imports'],
        queryFn: () => api.botImports(),
        staleTime: 0,
      });
      // Пустой черновик с тем же названием, иначе любой пустой: новый упёрся бы в потолок
      // из трёх незавершённых.
      const empty = fresh.filter((i) => i.status === 'draft' && i.rows === 0 && !i.bot_id);
      const reuse = empty.find((i) => (i.source_name ?? '') === source.trim()) ?? empty[0];
      const id = reuse ? reuse.id : (await api.createBotImport({ source_name: source.trim() || null })).id;
      idRef.current = id;
      setCreated(true);
      if (!reuse) void qc.invalidateQueries({ queryKey: ['bot-imports'] });
      return id;
    })().finally(() => {
      creating.current = null;
    });
    return creating.current;
  };

  return (
    <div className="p-4 space-y-4 pb-32">
      <PageTitle />
      <Stepper current="base" imp={null} />
      <StepCard n={1} state="current">
        <Field label="С какой платформы переезжаете" hint="Необязательно, нам так проще помочь.">
          <input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            maxLength={64}
            placeholder="Название платформы"
            readOnly={created}
            className="w-full read-only:opacity-60 surface-inset rounded-[13px] px-4 py-3 text-[16px] focus:outline-none focus:border-white/30"
          />
        </Field>
        <Uploader
          ensureId={ensureId}
          wantsTransfer={null}
          replacing={false}
          locked={null}
          onDone={(id) => void navigate({ to: '/bot-import', search: { id } })}
        />
      </StepCard>
    </div>
  );
}

function ImportList({ title, items }: { title: string; items: BotImport[] }) {
  const navigate = useNavigate();
  return (
    <section className="space-y-2">
      <div className="eyebrow px-1">{title}</div>
      {items.map((imp) => (
        <button
          key={imp.id}
          type="button"
          onClick={() => {
            haptic('light');
            void navigate({ to: '/bot-import', search: { id: imp.id } });
          }}
          className="w-full surface-card rounded-[18px] p-3.5 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
        >
          <div className="w-10 h-10 rounded-[12px] bg-white/[0.07] text-primary-light flex items-center justify-center shrink-0">
            <Truck className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-[14px] truncate">
              {imp.bot_username ? `@${imp.bot_username}` : imp.rows > 0 ? 'Бот не подключён' : 'База не загружена'}
            </div>
            <div className="text-[11.5px] text-dim truncate mt-0.5">
              {[imp.source_name, fmtDate(imp.created_at)].filter(Boolean).join(' · ')}
            </div>
          </div>
          <StatusPill status={imp.status} />
          <ChevronRight className="w-4 h-4 text-dim shrink-0" />
        </button>
      ))}
    </section>
  );
}

function StatusPill({ status }: { status: BotImportStatus }) {
  const tone =
    status === 'paused' || status === 'awaiting_payment'
      ? 'bg-[rgba(240,176,78,0.14)] text-amber'
      : status === 'cancelled'
        ? 'bg-white/[0.04] text-dim'
        : 'bg-white/[0.07] text-muted-foreground';
  return (
    <span className={cn('text-[10.5px] font-bold px-2 py-1 rounded-full shrink-0 whitespace-nowrap', tone)}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

// ---------------------------------------------------------------- выбор бота

type BotMode = 'token' | 'existing';
type BotPick = { mode: BotMode; token: string; botId: string };
const EMPTY_PICK: BotPick = { mode: 'token', token: '', botId: '' };

function pickBody(p: BotPick): { token?: string; bot_id?: string } {
  if (p.mode === 'token') return { token: p.token.trim() };
  return { bot_id: p.botId };
}

function pickReady(p: BotPick): boolean {
  if (p.mode === 'token') return p.token.trim().length > 0;
  return p.botId !== '';
}

/** Токен из BotFather копируют вместе с пробелом или переводом строки: края срезаем. */
function pickProblem(p: BotPick): string | null {
  if (p.mode === 'token' && !TOKEN_RE.test(p.token.trim())) {
    return 'Так токен не выглядит. Скопируйте его из @BotFather целиком, вместе с цифрами до двоеточия';
  }
  return null;
}

/** Боты, у которых уже идёт переезд: второй бэк не откроет. */
function busyBots(imports: BotImport[] | undefined, except?: string): Set<string> {
  const out = new Set<string>();
  for (const i of imports ?? []) {
    if (i.id !== except && i.bot_id && OPEN_STATUSES.has(i.status)) out.add(i.bot_id);
  }
  return out;
}

function BotPicker({
  value,
  onChange,
  busyBotIds,
}: {
  value: BotPick;
  onChange: (next: BotPick) => void;
  busyBotIds: Set<string>;
}) {
  const botsQ = useQuery({ queryKey: ['bots'], queryFn: () => api.bots() });
  const bots = (botsQ.data ?? []).filter((b) => !busyBotIds.has(b.id));

  const options: { mode: BotMode; title: string; text: string }[] = [
    {
      mode: 'token',
      title: 'Новый токен',
      text: 'Тот же бот с тем же @username: выпустите в @BotFather новый токен и вставьте его',
    },
  ];
  // Своих ботов нет: вариант «уже подключён» только сбил бы с толку.
  if (botsQ.isPending || bots.length > 0) {
    options.push({
      mode: 'existing',
      title: 'Бот уже подключён',
      text: 'Вы уже подключили его к нам: перенесём в него базу и подписки',
    });
  }

  return (
    <div className="space-y-3">
      <p className="text-[13px] text-muted-foreground leading-snug">
        Клиенты переезжают вместе с ботом, поэтому нужен тот самый бот, в котором они сидят.
      </p>
      <div className="space-y-2">
        {options.map((o) => {
          const active = value.mode === o.mode;
          return (
            <button
              key={o.mode}
              type="button"
              aria-pressed={active}
              onClick={() => {
                if (active) return;
                haptic('selection');
                onChange({ ...value, mode: o.mode });
              }}
              className={cn(
                'w-full text-left rounded-[14px] px-3.5 py-3 flex gap-3 items-start transition-colors',
                active ? 'bg-white/[0.07] border border-white/25' : 'surface-inset',
              )}
            >
              <span
                className={cn(
                  'mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center',
                  active ? 'border-white/30' : 'border-white/25',
                )}
              >
                {active && <span className="w-1.5 h-1.5 rounded-full bg-foreground" />}
              </span>
              <span className="min-w-0">
                <span className="block text-[13.5px] font-bold">{o.title}</span>
                <span className="block text-[12px] text-dim leading-snug mt-0.5">{o.text}</span>
              </span>
            </button>
          );
        })}
      </div>

      {value.mode === 'token' && (
        <TokenInput value={value.token} onChange={(token) => onChange({ ...value, token })} />
      )}

      {value.mode === 'existing' &&
        (botsQ.isPending ? (
          <Skeleton className="h-[46px] w-full rounded-[13px]" />
        ) : bots.length === 0 ? (
          <p className="text-[12px] text-dim leading-snug px-1">
            Свободных ботов нет: у каждого уже идёт переезд. Подключите бота новым токеном.
          </p>
        ) : (
          <select
            value={value.botId}
            onChange={(e) => onChange({ ...value, botId: e.target.value })}
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
          >
            <option value="">Выберите бота</option>
            {bots.map((b) => (
              <option key={b.id} value={b.id}>
                @{b.username}
                {b.status === 'paused' ? ' (на паузе)' : ''}
              </option>
            ))}
          </select>
        ))}

    </div>
  );
}

/**
 * Чтение буфера с подсказкой по таймеру, как в онбординге: `readText()` умеет висеть
 * вечно, если разрешение застряло в «prompt», а показать запрос вебвью негде. Таймер
 * при этом чтение не отменяет: человек может думать над запросом секунды.
 */
function pasteFromClipboard(onValue: (v: string) => void): void {
  let settled = false;
  const hint = setTimeout(() => {
    if (!settled) toast('Разрешите доступ к буферу или вставьте токен вручную');
  }, 2500);

  let reading: Promise<string>;
  try {
    reading = navigator.clipboard.readText();
  } catch {
    clearTimeout(hint);
    toast.error('Не удалось прочитать буфер: вставьте токен вручную');
    return;
  }
  reading
    .then((text) => {
      settled = true;
      clearTimeout(hint);
      const clean = text.trim();
      if (!clean) {
        toast.error('Буфер обмена пуст');
        return;
      }
      onValue(clean);
      haptic('success');
    })
    .catch(() => {
      settled = true;
      clearTimeout(hint);
      toast.error('Не удалось прочитать буфер: вставьте токен вручную');
    });
}

function TokenInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const inputId = useId();
  return (
    <div className="space-y-3">
      <BotFatherChecklist />
      <div className="surface-inset rounded-[14px] p-3.5 space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <label htmlFor={inputId} className="text-[12px] font-bold text-muted-foreground">
            Новый токен бота
          </label>
          {/* Токен всегда вставляют, а не набирают: на мобиле долгий тап по полю то и
              дело перехватывается выделением. */}
          <button
            type="button"
            onClick={() => pasteFromClipboard(onChange)}
            className="text-xs text-foreground px-2 py-1 -mr-2 rounded-lg active:scale-95 transition-transform"
          >
            Вставить
          </button>
        </div>
        <input
          id={inputId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          // Токен не текст на языке: автозамена и заглавные его ломают.
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="123456789:AAE…"
          className="w-full bg-white/5 border border-white/5 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-white/30 text-foreground placeholder-muted-foreground"
        />
      </div>
    </div>
  );
}

function BotFatherChecklist() {
  const openBotFather = () => {
    const tg = getWebApp();
    if (tg?.openTelegramLink) tg.openTelegramLink('https://t.me/BotFather');
    else window.open('https://t.me/BotFather', '_blank');
  };
  return (
    <div className="surface-inset rounded-[14px] p-3.5 space-y-3">
      <ol className="space-y-2.5">
        <ChecklistItem n={1}>
          В @BotFather: /mybots → ваш бот → API Token → Revoke current token. Старая платформа сразу
          потеряет доступ к боту.
        </ChecklistItem>
        <ChecklistItem n={2}>Вставьте новый токен ниже.</ChecklistItem>
        <ChecklistItem n={3}>
          В @BotFather → Bot Settings → Configure Mini App уберите ссылку старой платформы, если она
          там есть.
        </ChecklistItem>
        <ChecklistItem n={4}>
          Там же в Edit Bot проверьте описание бота: в нём могут остаться контакты старой платформы.
        </ChecklistItem>
      </ol>
      <button
        type="button"
        onClick={openBotFather}
        className="w-full bg-white/[0.06] py-2.5 rounded-[12px] text-[13px] font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
      >
        <ExternalLink className="w-4 h-4" />
        Открыть @BotFather
      </button>
    </div>
  );
}

function ChecklistItem({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex gap-2.5 items-start">
      <span className="w-6 h-6 bg-white/[0.07] text-foreground rounded-lg flex items-center justify-center font-bold text-[12px] shrink-0">
        {n}
      </span>
      <span className="text-[12.5px] leading-relaxed pt-0.5">{children}</span>
    </li>
  );
}

// ---------------------------------------------------------------- один переезд

function ImportScreen({ id }: { id: string }) {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['bot-import', id],
    queryFn: () => api.botImport(id),
    // Кэш кабинета живёт в sessionStorage: без этого экран показал бы вчерашний шаг.
    staleTime: 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && POLL_STATUSES.has(status) ? POLL_MS : false;
    },
  });

  if (q.isPending) return <ImportSkeleton />;

  const imp = q.data;
  if (!imp) {
    const notFound =
      errCode(q.error) === 'app.bot_import.not_found' ||
      (q.error as { status?: number } | null)?.status === 404;
    return (
      <div className="p-4 space-y-4 pb-32">
        <PageTitle />
        <div className="surface-card rounded-[22px] p-6 text-center space-y-3">
          <div className="font-bold text-[15px]">
            {notFound ? 'Переезд не найден' : 'Не удалось загрузить переезд'}
          </div>
          <p className="text-[12.5px] text-dim leading-snug">
            {notFound
              ? 'Возможно, ссылка из другого кабинета. Откройте список своих переездов.'
              : 'Проверьте связь и попробуйте ещё раз.'}
          </p>
          <button
            type="button"
            onClick={() => {
              if (notFound) void navigate({ to: '/bot-import', search: {} });
              else void q.refetch();
            }}
            className="btn-glass-red px-5 py-2.5 rounded-[12px] text-[13px] font-semibold active:scale-[0.98] transition-transform"
          >
            {notFound ? 'К переездам' : 'Повторить'}
          </button>
        </div>
      </div>
    );
  }

  const cancelled = imp.status === 'cancelled';
  const current = currentStep(imp);
  const curIdx = STEPS.findIndex((s) => s.key === current);
  const live = OPEN_STATUSES.has(imp.status);

  return (
    <div className="p-4 space-y-4 pb-32">
      <PageTitle>
        <div className="flex items-center gap-2 mt-1.5 min-w-0">
          <StatusPill status={imp.status} />
          {imp.source_name && (
            <span className="text-[12px] text-dim truncate">Откуда: {imp.source_name}</span>
          )}
        </div>
      </PageTitle>
      <Stepper current={cancelled ? null : current} imp={imp} />

      {STEPS.map((step, i) => {
        // Пройденные шаги свёрнуты в строку, будущие не показываются вовсе: их видно в
        // полоске сверху, а содержимое без предыдущих шагов только сбивает.
        if (cancelled) return stepDone(imp, step.key) && step.key !== 'transfer' ? doneRow(imp, step.key) : null;
        if (i < curIdx) return doneRow(imp, step.key);
        if (i > curIdx) return null;
        if (step.key === 'base') return <BaseStep key="base" imp={imp} />;
        if (step.key === 'bot') return <BotStep key="bot" imp={imp} />;
        if (step.key === 'subs') return <SubsStep key="subs" imp={imp} />;
        return <TransferStep key="transfer" imp={imp} />;
      })}

      {cancelled && (
        <section className="surface-card rounded-[20px] p-4">
          <Cancelled imp={imp} />
        </section>
      )}
      {/* Бот продаёт с момента подключения, а наценка у нового стоит по умолчанию,
          не партнёрская. Ждать конца переезда, чтобы узнать это, незачем. */}
      {live && imp.bot_id && current !== 'bot' && <PricingLink botId={imp.bot_id} />}
    </div>
  );
}

function doneRow(imp: BotImport, key: StepKey): ReactNode {
  if (key === 'base') return <BaseStep key="base" imp={imp} collapsed />;
  if (key === 'bot') {
    return (
      <DoneRow key="bot" n={2} title="Бот">
        {imp.bot_username ? `@${imp.bot_username}` : 'Подключён'}
      </DoneRow>
    );
  }
  if (key === 'subs') {
    return (
      <DoneRow key="subs" n={3} title="Подписки">
        {subsSummary(imp)}
      </DoneRow>
    );
  }
  return null;
}

function subsSummary(imp: BotImport): string {
  if (imp.payer === 'free') return 'За наш счёт';
  if (imp.payer === 'paid') {
    return imp.paid_display_minor != null
      ? `Оплачено ${fmtMoney(imp.paid_display_minor, imp.display_currency)}`
      : 'Оплачено';
  }
  return 'Не переносим';
}

/** Полоска шагов: где вы сейчас и сколько осталось. */
function Stepper({ current, imp }: { current: StepKey | null; imp: BotImport | null }) {
  return (
    <ol className="grid grid-cols-4 gap-1.5" aria-label="Шаги переезда">
      {STEPS.map((s, i) => {
        const done = imp ? stepDone(imp, s.key) && s.key !== current : false;
        const active = s.key === current;
        return (
          <li key={s.key} aria-current={active ? 'step' : undefined} className="min-w-0">
            <div
              className={cn(
                'h-[3px] rounded-full',
                active ? 'bg-foreground' : done ? 'bg-white/40' : 'bg-white/[0.08]',
              )}
            />
            <div
              className={cn(
                'mt-1.5 flex items-center gap-1 text-[11.5px] truncate',
                active ? 'text-foreground font-semibold' : done ? 'text-muted-foreground' : 'text-dim',
              )}
            >
              {done ? <Check className="w-3 h-3 shrink-0" /> : <span className="nums">{i + 1}</span>}
              <span className="truncate">{s.short}</span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

type StepState = 'done' | 'current';

function StepCard({ n, state, children }: { n: 1 | 2 | 3 | 4; state: StepState; children: ReactNode }) {
  return (
    <section className="surface-card rounded-[20px] p-4 space-y-3">
      <div className="flex items-center gap-2.5">
        <span className="w-7 h-7 rounded-lg flex items-center justify-center font-semibold text-sm shrink-0 bg-white/[0.07] text-foreground">
          {state === 'done' ? <Check className="w-4 h-4" /> : n}
        </span>
        <h3 className="font-display font-medium text-[16px]">{STEPS[n - 1].title}</h3>
      </div>
      {children}
    </section>
  );
}

/** Пройденный шаг одной строкой: номер с галочкой, название, итог и, если ещё можно,
 *  «Изменить». */
function DoneRow({
  n,
  title,
  children,
  onEdit,
}: {
  n: 1 | 2 | 3 | 4;
  title: string;
  children: ReactNode;
  onEdit?: () => void;
}) {
  return (
    <section className="surface-card rounded-[16px] px-4 py-3 flex items-center gap-3" aria-label={`Шаг ${n}: ${title}`}>
      <span className="w-6 h-6 rounded-md bg-white/[0.07] flex items-center justify-center shrink-0">
        <Check className="w-3.5 h-3.5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] text-dim">{title}</div>
        <div className="text-[14px] truncate">{children}</div>
      </div>
      {onEdit && (
        <button
          type="button"
          onClick={() => {
            haptic('light');
            onEdit();
          }}
          className="shrink-0 text-[13px] font-semibold text-muted-foreground px-2 py-2 -mr-2 active:scale-95 transition-transform"
        >
          Изменить
        </button>
      )}
    </section>
  );
}

// ---- 2. Бот

function BotStep({ imp }: { imp: BotImport }) {
  return (
    <StepCard n={2} state="current">
      <ConnectBotForm imp={imp} />
    </StepCard>
  );
}

function ConnectBotForm({ imp }: { imp: BotImport }) {
  const qc = useQueryClient();
  const [pick, setPick] = useState<BotPick>(EMPTY_PICK);
  const [error, setError] = useState<string | null>(null);
  const listQ = useQuery({
    queryKey: ['bot-imports'],
    queryFn: () => api.botImports(),
    staleTime: 0,
  });

  const connect = useMutation({
    mutationFn: () => api.connectBotImportBot(imp.id, pickBody(pick)),
    onSuccess: (dto) => {
      haptic('success');
      toast.success(dto.bot_username ? `@${dto.bot_username} подключён` : 'Бот подключён');
      setImport(qc, dto);
      void qc.invalidateQueries({ queryKey: ['bots'] });
    },
    onError: (e: Error) => {
      haptic('error');
      setError(errorText(e, 'Не удалось подключить бота'));
      refreshOnState(qc, e, imp.id);
    },
  });

  const submit = () => {
    const problem = pickProblem(pick);
    if (problem) {
      haptic('error');
      setError(problem);
      return;
    }
    setError(null);
    connect.mutate();
  };

  return (
    <div className="space-y-3">
      {subsSettled(imp) && imp.payer !== 'none' && (
        <p className="text-[12.5px] text-muted-foreground leading-snug">
          С подписками всё решено, ждём только бота: без него переносить некуда.
        </p>
      )}
      {/* Переезды, начатые до 18.10.2026, могли дойти до решения по подпискам без бота. */}
      {(imp.status === 'awaiting_decision' || imp.status === 'awaiting_payment') && (
        <p className="text-[12.5px] text-muted-foreground leading-snug">
          {imp.status === 'awaiting_payment'
            ? 'Подключите бота, после этого откроется оплата переноса.'
            : 'Решение по подпискам пришлём, а пока подключите бота.'}
        </p>
      )}
      <BotPicker
        value={pick}
        onChange={(next) => {
          setPick(next);
          setError(null);
        }}
        busyBotIds={busyBots(listQ.data, imp.id)}
      />
      <button
        type="button"
        onClick={submit}
        disabled={connect.isPending || !pickReady(pick)}
        className="btn-glass-red w-full h-12 rounded-[13px] text-[15px] font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
      >
        {connect.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
        Подключить бота
      </button>
      {error && <ErrorLine text={error} />}
    </div>
  );
}

function PricingLink({ botId }: { botId: string }) {
  const navigate = useNavigate();
  return (
    <div className="surface-inset rounded-[13px] p-3 flex items-center gap-3">
      <p className="flex-1 min-w-0 text-[12px] text-dim leading-snug">
        Бот уже продаёт с наценкой по умолчанию. Поставьте свою.
      </p>
      <button
        type="button"
        onClick={() => {
          haptic('light');
          void navigate({ to: '/pricing', search: { botId } });
        }}
        className="glass glass-flat shrink-0 px-3 py-2 rounded-[11px] text-[12.5px] font-semibold flex items-center gap-1.5 active:scale-95 transition-transform"
      >
        <Tags className="w-3.5 h-3.5" />
        Цены
      </button>
    </div>
  );
}

// ---- 1. База клиентов

function BaseStep({ imp, collapsed = false }: { imp: BotImport; collapsed?: boolean }) {
  const [replacing, setReplacing] = useState(false);
  const canUpload = canUploadRows(imp);
  const hasBase = imp.rows > 0;

  // Форму, которую партнёр открыл сам, опрос не сносит: если за это время базу
  // заперли (пришла оплата, начался перенос), форма остаётся и объясняет почему.
  // Иначе выбранный файл и разметка исчезли бы молча.
  if (collapsed && !replacing) {
    return (
      <DoneRow n={1} title="База клиентов" onEdit={canUpload ? () => setReplacing(true) : undefined}>
        {hasBase ? (
          <>
            <span className="nums">{imp.clients.toLocaleString('ru-RU')}</span>{' '}
            {plural(imp.clients, 'клиент', 'клиента', 'клиентов')}
          </>
        ) : (
          'Не загружали'
        )}
      </DoneRow>
    );
  }

  return (
    <StepCard n={1} state="current">
      <Uploader
        ensureId={() => Promise.resolve(imp.id)}
        wantsTransfer={imp.wants_transfer}
        replacing={hasBase}
        locked={canUpload ? null : lockedText(imp)}
        onCancel={replacing ? () => setReplacing(false) : undefined}
        onDone={() => setReplacing(false)}
      />
    </StepCard>
  );
}

/** Почему базу уже не заменить, если форма замены была открыта до этого. */
function lockedText(imp: BotImport): string {
  if (imp.status === 'cancelled') return 'Переезд отменён: базу уже не заменить.';
  const why =
    imp.paid_display_minor != null ? 'Перенос подписок оплачен' : 'Перенос подписок уже начался';
  return `${why}, и базу теперь меняем только мы. Если нужно что-то поправить, напишите в поддержку.`;
}

function Uploader({
  ensureId,
  wantsTransfer,
  replacing,
  locked,
  onCancel,
  onDone,
}: {
  /** Адрес переезда. До первой базы переезда ещё нет, и он заводится здесь. */
  ensureId: () => Promise<string>;
  wantsTransfer: boolean | null;
  /** Базу уже загружали: новый файл её заменит. */
  replacing: boolean;
  /** Базу заперли, пока форма была открыта: текст причины вместо формы. */
  locked: string | null;
  onCancel?: () => void;
  onDone: (importId: string) => void;
}) {
  const qc = useQueryClient();
  const idRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [mapping, setMapping] = useState<BotImportMapping>({});
  const [preview, setPreview] = useState<BotImportRowsPreview | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Номер последней проверки: ответ на устаревшую разметку не должен затереть свежий.
  const seq = useRef(0);
  useEffect(
    () => () => {
      seq.current += 1;
    },
    [],
  );

  // Проверка ничего не сохраняет: бэк разбирает файл и отвечает, как понял колонки.
  const check = async (f: File, m: BotImportMapping | null) => {
    const my = ++seq.current;
    setChecking(true);
    setError(null);
    try {
      const id = await ensureId();
      idRef.current = id;
      const p = await api.uploadBotImportRows(id, f, { dryRun: true, mapping: m });
      if (my !== seq.current) return;
      setPreview(p);
      setMapping(p.mapping);
    } catch (e) {
      if (my !== seq.current) return;
      haptic('error');
      setPreview(null);
      setError(errorText(e, 'Не удалось прочитать файл'));
      if (idRef.current) refreshOnState(qc, e, idRef.current);
    } finally {
      if (my === seq.current) setChecking(false);
    }
  };

  const pickFile = (f: File | null) => {
    // Сбрасываем значение поля: иначе повторный выбор того же файла не придёт в onChange.
    if (inputRef.current) inputRef.current.value = '';
    if (!f) return;
    setPreview(null);
    setMapping({});
    if (f.size > MAX_FILE_BYTES) {
      haptic('error');
      setFile(null);
      setError(FILE_TOO_BIG);
      return;
    }
    setFile(f);
    void check(f, null);
  };

  const setRole = (role: BotImportRole, col: number | null) => {
    const next: BotImportMapping = { ...mapping };
    delete next[role];
    if (col !== null) {
      // Одна колонка, одна роль: выбранную колонку снимаем с прежней роли.
      for (const r of Object.keys(next) as BotImportRole[]) {
        if (next[r] === col) delete next[r];
      }
      next[role] = col;
    }
    setMapping(next);
    // Без Telegram ID бэку разбирать нечего: пустую разметку он заменил бы своей догадкой.
    if (file && next.tg_id !== undefined) {
      void check(file, next);
    } else {
      // Проверка по прежней разметке ещё может быть в пути: её ответ вернул бы
      // колонку, которую только что сняли.
      seq.current += 1;
      setChecking(false);
    }
  };

  const commit = useMutation({
    mutationFn: async (f: File) => {
      const id = await ensureId();
      idRef.current = id;
      return { id, p: await api.uploadBotImportRows(id, f, { dryRun: false, mapping }) };
    },
    onSuccess: ({ id, p }) => {
      haptic('success');
      toast.success(`База загружена: ${p.clients} ${plural(p.clients, 'клиент', 'клиента', 'клиентов')}`);
      if (p.import) setImport(qc, p.import);
      else void qc.invalidateQueries({ queryKey: ['bot-import', id] });
      setFile(null);
      setPreview(null);
      setMapping({});
      onDone(id);
    },
    onError: (e: Error) => {
      haptic('error');
      setError(errorText(e, 'Не удалось загрузить базу'));
      if (idRef.current) refreshOnState(qc, e, idRef.current);
    },
  });

  const tgChosen = mapping.tg_id !== undefined;
  const canCommit =
    file !== null &&
    preview !== null &&
    tgChosen &&
    !checking &&
    preview.rows_valid > 0 &&
    !commit.isPending;

  if (locked) {
    return (
      <div className="space-y-3">
        <Warn>{locked}</Warn>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="w-full bg-white/[0.04] py-2.5 rounded-[12px] text-[13px] font-bold active:scale-[0.98] transition-transform"
          >
            Закрыть
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {!file ? (
        <>
          <p className="text-[13px] text-muted-foreground leading-snug">
            Выгрузите клиентов из старой панели: CSV, Excel (.xlsx), JSON или TXT до 10 МБ. Нужна
            хотя бы колонка с Telegram ID. По дате окончания и тарифу перенесём действующие
            подписки.
          </p>
          {replacing && (
            <Warn>
              Новый файл заменит загруженную базу.
              {wantsTransfer === true && ' Решение по подпискам сбросится: условия переноса посчитаем заново.'}
            </Warn>
          )}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="btn-glass-red w-full h-12 rounded-[13px] text-[15px] font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          >
            <FileUp className="w-4 h-4" />
            Выбрать файл
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="glass glass-flat w-full py-2.5 rounded-[12px] text-[13px] font-semibold active:scale-[0.98] transition-transform"
            >
              {replacing ? 'Оставить прежнюю базу' : 'Отмена'}
            </button>
          )}
        </>
      ) : (
        <>
          <div className="surface-inset rounded-[13px] px-3.5 py-3 flex items-center gap-3">
            <FileUp className="w-4 h-4 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold truncate">{file.name}</div>
              <div className="text-[11px] text-dim">
                {fmtBytes(file.size)}
                {checking && ' · читаем файл…'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                seq.current += 1;
                setChecking(false);
                setFile(null);
                setPreview(null);
                setMapping({});
                setError(null);
              }}
              disabled={commit.isPending}
              aria-label="Убрать файл"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-dim disabled:opacity-40 active:scale-95"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {!preview && checking && (
            <div className="space-y-2">
              <SkeletonLine className="w-2/3" />
              <SkeletonLine size="sm" className="w-1/2" />
            </div>
          )}

          {preview && (
            <>
              <MappingEditor preview={preview} mapping={mapping} onChange={setRole} />
              {tgChosen ? (
                <PreviewStats preview={preview} checking={checking} />
              ) : (
                <Warn>Выберите колонку с Telegram ID: без неё базу не загрузить.</Warn>
              )}
              <SampleTable preview={preview} mapping={mapping} />
              {tgChosen && !checking && <ParseErrors preview={preview} />}
            </>
          )}

          <button
            type="button"
            onClick={() => file && commit.mutate(file)}
            disabled={!canCommit}
            className="btn-glass-red w-full h-12 rounded-[13px] text-[15px] font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
          >
            {commit.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Загрузить базу
          </button>
          {preview && tgChosen && preview.rows_valid === 0 && !checking && (
            <p className="text-[12px] text-dim leading-snug px-1">
              В выбранной колонке нет ни одного Telegram ID. Выберите другую.
            </p>
          )}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={commit.isPending}
              className="w-full bg-white/[0.04] py-2.5 rounded-[12px] text-[13px] font-bold disabled:opacity-50 active:scale-[0.98] transition-transform"
            >
              {replacing ? 'Оставить прежнюю базу' : 'Отмена'}
            </button>
          )}
        </>
      )}
      {error && <ErrorLine text={error} />}
      <input
        ref={inputRef}
        type="file"
        accept={FILE_ACCEPT}
        className="hidden"
        onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
      />
    </div>
  );
}

/** Подпись колонки в списке: заголовок и первое значение, чтобы узнать её с ходу. */
function columnLabel(preview: BotImportRowsPreview, i: number): string {
  const head = preview.headers[i] ?? `Колонка ${i + 1}`;
  const sample = preview.sample.find((r) => (r[i] ?? '').trim() !== '')?.[i]?.trim();
  if (!sample) return head;
  const short = sample.length > 18 ? `${sample.slice(0, 18)}…` : sample;
  return `${head} · ${short}`;
}

function MappingEditor({
  preview,
  mapping,
  onChange,
}: {
  preview: BotImportRowsPreview;
  mapping: BotImportMapping;
  onChange: (role: BotImportRole, col: number | null) => void;
}) {
  return (
    <div className="surface-inset rounded-[14px] p-3.5 space-y-2.5">
      <div>
        <div className="text-[12px] font-bold text-muted-foreground">Колонки</div>
        <p className="text-[11.5px] text-dim leading-snug mt-0.5">
          Определили сами. Проверьте и поправьте, если что-то не так.
        </p>
      </div>
      {ROLES.map(({ role, label }) => (
        <label key={role} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] items-center gap-2.5">
          <span className="text-[12.5px] leading-tight">
            {label}
            {role === 'tg_id' && <span className="block text-[10.5px] text-dim">обязательно</span>}
          </span>
          <select
            value={mapping[role] ?? ''}
            onChange={(e) => onChange(role, e.target.value === '' ? null : Number(e.target.value))}
            className="w-full min-w-0 surface-inset rounded-[11px] px-3 py-2 text-[13px] focus:outline-none"
          >
            <option value="">нет</option>
            {preview.headers.map((_, i) => (
              <option key={i} value={i}>
                {columnLabel(preview, i)}
              </option>
            ))}
          </select>
        </label>
      ))}
      <p className="text-[11px] text-dim leading-snug">
        Баланс клиентов сам не переносится: сумма из файла останется у нас для справки.
      </p>
    </div>
  );
}

function PreviewStats({ preview: p, checking }: { preview: BotImportRowsPreview; checking: boolean }) {
  return (
    <div className={cn('space-y-2 transition-opacity', checking && 'opacity-50')}>
      <div className="grid grid-cols-2 gap-2">
        <Stat label="Строк" value={p.rows_valid} />
        <Stat label="Клиентов" value={p.clients} />
        <Stat label="С действующей подпиской" value={p.with_subscription} />
        <Stat label="Ошибок" value={p.error_count} warn={p.error_count > 0} />
      </div>
      {checking && (
        <p className="text-[11.5px] text-dim flex items-center gap-1.5 px-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          Пересчитываем по новой разметке…
        </p>
      )}
      {p.duplicates > 0 && (
        <p className="text-[11.5px] text-dim leading-snug px-1">
          Одинаковых строк: {p.duplicates.toLocaleString('ru-RU')}, каждую учли один раз.
        </p>
      )}
      {p.truncated && (
        <Warn>Файл длиннее 50 000 строк: загрузятся только первые 50 000.</Warn>
      )}
      {(p.warnings ?? []).map((w) => (
        <Warn key={w}>{w}</Warn>
      ))}
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className="surface-inset rounded-[13px] p-3">
      <div className="text-[11px] text-dim leading-tight">{label}</div>
      <div className={cn('font-display font-bold text-[18px] nums mt-1', warn && 'text-amber')}>
        {value.toLocaleString('ru-RU')}
      </div>
    </div>
  );
}

function ParseErrors({ preview: p }: { preview: BotImportRowsPreview }) {
  const [open, setOpen] = useState(false);
  if (p.error_count === 0) return null;
  const rest = p.error_count - p.errors.length;
  return (
    <div className="surface-inset rounded-[13px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3.5 py-3 text-[12.5px] font-bold text-amber"
      >
        <span className="flex items-center gap-2 text-left">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Строки с ошибками: {p.error_count.toLocaleString('ru-RU')}
        </span>
        <ChevronDown className={cn('w-4 h-4 shrink-0 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <ul className="px-3.5 pb-3 space-y-1.5 text-[12px] text-muted-foreground leading-snug">
          {p.errors.map((er, i) => (
            <li key={`${er.line}-${i}`}>
              <span className="text-dim nums">Строка {er.line}:</span> {er.message}
            </li>
          ))}
          {rest > 0 && <li className="text-dim">и ещё {rest.toLocaleString('ru-RU')}</li>}
        </ul>
      )}
    </div>
  );
}

function SampleTable({ preview: p, mapping }: { preview: BotImportRowsPreview; mapping: BotImportMapping }) {
  const rows = p.sample.slice(0, 5);
  if (rows.length === 0) return null;
  const roleOf = (i: number) => ROLES.find((r) => mapping[r.role] === i)?.label;
  return (
    <div className="space-y-1.5">
      <div className="text-[12px] font-bold text-muted-foreground px-1">Первые строки файла</div>
      <div className="surface-inset rounded-[13px] overflow-x-auto overscroll-x-contain">
        <table className="min-w-full text-[11.5px]">
          <thead>
            <tr className="border-b border-white/[0.06]">
              {p.headers.map((h, i) => (
                <th key={i} className="px-2.5 py-2 text-left font-semibold align-bottom whitespace-nowrap">
                  {roleOf(i) && (
                    <span className="block text-[10px] font-bold text-primary-light">{roleOf(i)}</span>
                  )}
                  <span className="text-muted-foreground">{h}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} className="border-b border-white/[0.04] last:border-0">
                {p.headers.map((_, ci) => (
                  <td
                    key={ci}
                    className={cn(
                      'px-2.5 py-1.5 max-w-[160px] truncate whitespace-nowrap nums',
                      roleOf(ci) ? 'text-foreground' : 'text-dim',
                    )}
                  >
                    {r[ci] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- 3. Подписки

function SubsStep({ imp }: { imp: BotImport }) {
  const qc = useQueryClient();
  const transfer = useMutation({
    mutationFn: (want: boolean) => api.botImportTransfer(imp.id, want),
    onSuccess: (dto) => {
      haptic('success');
      setImport(qc, dto);
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(errorText(e, 'Не удалось сохранить ответ'));
      refreshOnState(qc, e, imp.id);
    },
  });
  // Шаг раскрыт, только пока вопрос с подписками открыт: решённый сворачивается в
  // строку (`subsSummary`).
  let body: ReactNode;
  if (imp.status === 'awaiting_payment') {
    body = <PayBlock imp={imp} />;
  } else if (imp.status === 'awaiting_decision') {
    body = (
      <div className="space-y-3">
        <p className="text-[13.5px] leading-snug flex gap-2">
          <Loader2 className="w-4 h-4 animate-spin shrink-0 mt-0.5 text-muted-foreground" />
          <span>
            Решаем, какую часть переноса оплатим мы. Итог появится здесь, и мы пришлём
            сообщение в Telegram.
          </span>
        </p>
        <DeclineTransfer imp={imp} />
      </div>
    );
  } else if (imp.subs_total > 0) {
    const n = imp.subs_total;
    body = (
      <div className="space-y-3">
        <div>
          <div className="font-bold text-[14px]">Перенести действующие подписки клиентов?</div>
          <p className="text-[12.5px] text-muted-foreground leading-snug mt-1">
            В базе {n.toLocaleString('ru-RU')}{' '}
            {plural(n, 'действующая подписка', 'действующие подписки', 'действующих подписок')}.
            Перенос мы можем оплатить сами, целиком или частично: решим по вашей базе и
            напишем итог здесь и в Telegram. Отказаться можно и после.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => transfer.mutate(true)}
            disabled={transfer.isPending}
            className="btn-glass-red w-full h-12 rounded-[13px] text-[15px] font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
          >
            {transfer.isPending && transfer.variables === true && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            Да, узнать условия
          </button>
          <button
            type="button"
            onClick={() => transfer.mutate(false)}
            disabled={transfer.isPending}
            className="w-full bg-white/[0.04] py-2.5 rounded-[12px] text-[13px] font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
          >
            {transfer.isPending && transfer.variables === false && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            Нет, только клиентов
          </button>
        </div>
      </div>
    );
  } else {
    body = (
      <div className="space-y-3">
        <p className="text-[13.5px] leading-snug">
          В базе нет действующих подписок: клиенты переедут в бота без них.
        </p>
        <button
          type="button"
          onClick={() => transfer.mutate(false)}
          disabled={transfer.isPending}
          className="btn-glass-red w-full h-12 rounded-[13px] text-[15px] font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
        >
          {transfer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Продолжить без подписок
        </button>
      </div>
    );
  }

  return (
    <StepCard n={3} state="current">
      {body}
    </StepCard>
  );
}

function PayBlock({ imp }: { imp: BotImport }) {
  const qc = useQueryClient();
  const previewQ = useQuery({
    queryKey: ['bot-import-charge', imp.id],
    queryFn: () => api.botImportChargePreview(imp.id),
    staleTime: 0,
    // Сумма со временем только падает, а баланс могли пополнить в другой вкладке:
    // без обновления «не хватает» держало бы кнопку оплаты выключенной. Раз в минуту,
    // а не вместе с переездом: расчёт идёт по всем строкам базы.
    refetchInterval: 60_000,
  });
  const p = previewQ.data;
  // Пока партнёр не выбрал сам, а баланса не хватает, сразу предлагаем счёт.
  const [picked, setPicked] = useState<'balance' | 'invoice' | null>(null);
  const payWith = picked ?? (p && !p.enough ? 'invoice' : 'balance');
  const providersQ = useQuery({
    queryKey: ['partner-payment-providers'],
    queryFn: () => api.partnerPaymentProviders(),
    enabled: payWith === 'invoice',
  });

  const refreshMoney = () => {
    void qc.invalidateQueries({ queryKey: ['partner'] });
    void qc.invalidateQueries({ queryKey: ['partner-me'] });
  };

  const pay = useMutation({
    mutationFn: () => api.payBotImport(imp.id),
    onSuccess: (dto) => {
      haptic('success');
      toast.success('Перенос оплачен');
      setImport(qc, dto);
      refreshMoney();
    },
    onError: (e: Error) => {
      haptic('error');
      if (errCode(e) === 'app.bot_import.insufficient_balance') {
        // Баланс изменился между показом суммы и оплатой: остаёмся и предлагаем счёт.
        setPicked('invoice');
        void previewQ.refetch();
      }
      toast.error(errorText(e, 'Не удалось оплатить'));
      refreshOnState(qc, e, imp.id);
    },
  });

  const amount = p?.amount_display_minor ?? imp.price_display_minor;
  const count = p?.subs_count ?? imp.subs_total;
  const currency = p?.display_currency ?? imp.display_currency;

  return (
    <div className="space-y-4">
      {amount == null ? (
        <div className="space-y-2">
          <SkeletonLine className="w-3/4" />
          <SkeletonLine size="sm" className="w-1/2" />
        </div>
      ) : (
        <div className="surface-inset rounded-[13px] p-3.5">
          <p className="text-[14px] leading-snug">
            Перенос {count.toLocaleString('ru-RU')} {plural(count, 'подписки', 'подписок', 'подписок')}{' '}
            со скидкой обойдётся в <b className="nums">{fmtMoney(amount, currency)}</b>
          </p>
        </div>
      )}

      <div className="space-y-2">
        <div className="text-[12px] font-bold text-muted-foreground">Откуда платим</div>
        <div className="grid grid-cols-2 gap-[9px]">
          {(
            [
              { key: 'balance', label: 'С баланса' },
              { key: 'invoice', label: 'Оплатить счётом' },
            ] as const
          ).map((opt) => (
            <button
              type="button"
              key={opt.key}
              onClick={() => {
                haptic('selection');
                setPicked(opt.key);
              }}
              aria-pressed={payWith === opt.key}
              className={cn(
                'rounded-[14px] px-3 py-2.5 text-[13px] font-semibold transition-colors',
                payWith === opt.key
                  ? 'bg-white/[0.08] border border-white/30 text-foreground'
                  : 'surface-inset text-muted-foreground',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {payWith === 'balance' ? (
        <div className="space-y-3">
          {p && (
            <p className={cn('text-[11.5px] leading-snug px-1', p.enough ? 'text-dim' : 'text-amber')}>
              Доступно на балансе {fmtMoney(p.spendable_display_minor, p.display_currency)}.
              {!p.enough && ' Этого не хватает: оплатите счётом.'}
            </p>
          )}
          <button
            type="button"
            onClick={() => pay.mutate()}
            disabled={pay.isPending || !p || !p.enough}
            className="btn-glass-red w-full h-12 rounded-[13px] text-[15px] font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
          >
            {pay.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Оплатить с баланса
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-[11.5px] text-dim leading-snug px-1">
            Перенос начнётся сам, как только деньги придут.
          </p>
          <InvoicePay
            providers={providersQ.data?.providers}
            providersFailed={providersQ.isError}
            label="Оплатить перенос"
            buildBody={(provider) => ({
              purpose: 'bot_import' as const,
              provider,
              bot_import_id: imp.id,
            })}
            onPaid={() => {
              void qc.invalidateQueries({ queryKey: ['bot-import', imp.id] });
              void qc.invalidateQueries({ queryKey: ['bot-imports'] });
              refreshMoney();
            }}
          />
        </div>
      )}

      <DeclineTransfer imp={imp} />
    </div>
  );
}

/** Отказ от переноса. Вернуть подписки в переезд потом не выйдет, поэтому с вопросом. */
function DeclineTransfer({ imp }: { imp: BotImport }) {
  const qc = useQueryClient();
  const [asking, setAsking] = useState(false);
  const decline = useMutation({
    mutationFn: () => api.botImportTransfer(imp.id, false),
    onSuccess: (dto) => {
      haptic('success');
      toast.success('Подписки не переносим');
      setImport(qc, dto);
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(errorText(e, 'Не удалось сохранить ответ'));
      refreshOnState(qc, e, imp.id);
    },
  });

  if (!asking) {
    return (
      <button
        type="button"
        onClick={() => setAsking(true)}
        className="text-[12.5px] font-bold text-dim py-1 px-1 active:scale-95 transition-transform"
      >
        Не переносить подписки
      </button>
    );
  }
  return (
    <div className="surface-inset rounded-[13px] p-3.5 space-y-2.5">
      <p className="text-[12.5px] leading-snug">
        Не переносить подписки? Клиенты переедут в бота без них.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setAsking(false)}
          disabled={decline.isPending}
          className="flex-1 bg-white/[0.04] py-2.5 rounded-[12px] text-[13px] font-bold disabled:opacity-50"
        >
          Переносить
        </button>
        <button
          type="button"
          onClick={() => decline.mutate()}
          disabled={decline.isPending}
          className="flex-1 bg-white/[0.04] py-2.5 rounded-[12px] text-[13px] font-bold text-danger flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {decline.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Не переносить
        </button>
      </div>
    </div>
  );
}

// ---- 4. Перенос

function TransferStep({ imp }: { imp: BotImport }) {
  if (imp.status === 'running' || imp.status === 'paused') {
    return (
      <StepCard n={4} state="current">
        <Progress imp={imp} />
      </StepCard>
    );
  }
  if (imp.status === 'done') {
    return (
      <StepCard n={4} state="done">
        <Result imp={imp} />
      </StepCard>
    );
  }
  return (
    <StepCard n={4} state="current">
      <p className="text-[13px] text-dim leading-snug">Перенос вот-вот начнётся.</p>
    </StepCard>
  );
}

function Progress({ imp }: { imp: BotImport }) {
  const total = imp.subs_total;
  const pct = total > 0 ? Math.min(100, Math.round((imp.subs_done / total) * 100)) : 0;
  const paused = imp.status === 'paused';
  return (
    <div className="space-y-3">
      {paused && (
        <p className="text-[13px] leading-snug flex gap-2 text-amber">
          <PauseCircle className="w-4 h-4 shrink-0 mt-0.5" />
          Перенос приостановлен, мы уже разбираемся.
        </p>
      )}
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13.5px]">
          Перенесено <b className="nums">{imp.subs_done.toLocaleString('ru-RU')}</b> из{' '}
          <span className="nums">{total.toLocaleString('ru-RU')}</span>
        </span>
        <span className="font-display font-bold nums text-[15px]">{pct}%</span>
      </div>
      <div
        className="h-2 rounded-full bg-white/[0.06] overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={imp.subs_done}
      >
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-700 motion-reduce:transition-none',
            paused ? 'bg-amber' : 'bg-white/70',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      {!paused && (
        <p className="text-[12px] text-dim leading-snug">
          Каждому клиенту с перенесённой подпиской бот отправляет сообщение с новой ссылкой.
        </p>
      )}
      {imp.subs_problem > 0 && (
        <p className="text-[12px] text-amber leading-snug">
          Не перенеслось сразу: {imp.subs_problem.toLocaleString('ru-RU')}. Ими занимаемся мы,
          делать ничего не нужно.
        </p>
      )}
    </div>
  );
}

function Result({ imp }: { imp: BotImport }) {
  const navigate = useNavigate();
  // Ничего не рассылает: собирает заготовку и уводит в редактор рассылок, где партнёр
  // правит текст и жмёт «Отправить» сам. Рассылку не отозвать.
  const draft = useMutation({
    mutationFn: () => api.botImportBroadcastDraft(imp.id),
    onSuccess: ({ draft_token }) => {
      haptic('success');
      void navigate({ to: '/marketing', search: { bcastDraft: draft_token } });
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(errorText(e, 'Не удалось собрать рассылку'));
    },
  });

  return (
    <div className="space-y-3">
      <p className="text-[13.5px] font-bold">Переезд завершён.</p>
      {imp.subs_done > 0 && (
        <p className="text-[13px] leading-snug">
          Перенесено подписок: <b className="nums">{imp.subs_done.toLocaleString('ru-RU')}</b>.
        </p>
      )}
      {imp.subs_problem > 0 && (
        <p className="text-[12px] text-amber leading-snug">
          Не перенеслось сразу: {imp.subs_problem.toLocaleString('ru-RU')}. Ими занимаемся мы.
        </p>
      )}

      {imp.clients > 0 && (
        <div className="surface-inset rounded-[13px] p-3.5 space-y-2.5">
          <p className="text-[12px] text-dim leading-snug">
            {imp.subs_done > 0
              ? 'Мы писали только клиентам с перенесённой подпиской.'
              : 'Клиентам мы не писали.'}{' '}
            Рассылка откроется с готовым текстом о переезде: проверьте его и отправьте сами.
          </p>
          <button
            type="button"
            onClick={() => draft.mutate()}
            disabled={draft.isPending}
            className="btn-glass-red w-full h-12 rounded-[13px] text-[15px] font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
          >
            {draft.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Megaphone className="w-4 h-4" />
            )}
            Рассылка по всей базе
          </button>
        </div>
      )}

      {imp.bot_id && <PricingLink botId={imp.bot_id} />}
    </div>
  );
}

function Cancelled({ imp }: { imp: BotImport }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-2.5">
      <p className="text-[13px] leading-snug flex gap-2">
        <XCircle className="w-4 h-4 shrink-0 mt-0.5 text-dim" />
        <span>
          Переезд отменён.
          {imp.subs_done > 0 &&
            ` До отмены перенесли подписок: ${imp.subs_done.toLocaleString('ru-RU')}.`}
          {imp.paid_display_minor != null && ' Неизрасходованная часть оплаты вернулась на баланс.'}
        </span>
      </p>
      <button
        type="button"
        onClick={() => {
          haptic('light');
          void navigate({ to: '/bot-import', search: {} });
        }}
        className="text-[13px] font-semibold text-muted-foreground py-1 active:scale-95 transition-transform"
      >
        Начать новый переезд
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- мелочи

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[12px] font-bold text-muted-foreground">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-dim leading-snug">{hint}</div>}
    </div>
  );
}

function Warn({ children }: { children: ReactNode }) {
  return (
    <div className="flex gap-2 text-[12px] text-amber leading-snug px-1">
      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
      <span>{children}</span>
    </div>
  );
}

function ErrorLine({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 text-danger text-[12.5px] leading-snug">
      <XCircle className="w-4 h-4 shrink-0 mt-px" />
      <span className="break-words">{text}</span>
    </div>
  );
}

/** Экран переезда: заголовок и статус, полоска шагов и раскрытый текущий шаг. */
function ImportSkeleton() {
  return (
    <div className="p-4 space-y-4 pb-32">
      <PageTitle>
        <Skeleton className="h-[22px] w-28 rounded-full mt-1.5" />
      </PageTitle>
      <Stepper current={null} imp={null} />
      <SkeletonCard className="rounded-[20px] min-h-0 p-4 space-y-3">
        <div className="flex items-center gap-2.5">
          <Skeleton className="w-7 h-7 rounded-lg shrink-0" />
          <SkeletonLine className="w-32" />
        </div>
        <SkeletonLine className="w-3/4" />
        <SkeletonLine size="sm" className="w-1/2" />
        <Skeleton className="h-12 w-full rounded-[13px]" />
      </SkeletonCard>
    </div>
  );
}
